import {
  AllergyIntolerance,
  CodeableConcept,
  Coding,
  Patient,
  Encounter,
  AllergyIntoleranceReaction,
} from '@medplum/fhirtypes';
import { createReference, addProfileToResource } from '@medplum/core';
import {
  ALLERGY_INTOLERANCE_SEVERITY_EXTENSION_URL,
  EXTERNAL_CS,
  HL7,
  SYSTEMS,
} from '@consts/systems.consts';

// =============================================================================
// GETTERS - Safe access to nested fields
// =============================================================================

export function getAllergyCode(
  allergy: AllergyIntolerance
): CodeableConcept | undefined {
  return allergy.code;
}

export function getAllergyCodeText(
  allergy: AllergyIntolerance
): string | undefined {
  const code = getAllergyCode(allergy);
  return code?.text || code?.coding?.[0]?.display;
}

export function getCategory(
  allergy: AllergyIntolerance
): AllergyIntolerance['category'] | undefined {
  return allergy.category;
}

export function getClinicalStatus(
  allergy: AllergyIntolerance
): CodeableConcept | undefined {
  return allergy.clinicalStatus;
}

export function getSeverity(allergy: AllergyIntolerance): Coding | undefined {
  // First try to get from extension (full severity coding)
  const reaction = allergy.reaction?.[0];
  const extension = reaction?.extension?.find(
    (ext) => ext.url === ALLERGY_INTOLERANCE_SEVERITY_EXTENSION_URL
  );
  if (extension?.valueCoding) {
    return extension.valueCoding;
  }
  // Fallback to standard severity field
  if (reaction?.severity) {
    // Map back to full coding if we only have the severity string
    return {
      code: reaction.severity,
      system: SYSTEMS.SNOMED,
    };
  }
  return undefined;
}

export function getManifestation(
  allergy: AllergyIntolerance
): CodeableConcept | undefined {
  return allergy.reaction?.[0]?.manifestation?.[0];
}

/**
 * Get all reactions from an allergy.
 *
 * @param allergy - The allergy intolerance resource
 * @returns Array of reactions
 */
export function getReactions(
  allergy: AllergyIntolerance
): AllergyIntoleranceReaction[] {
  return allergy.reaction || [];
}

/**
 * Get a specific reaction by index.
 *
 * @param allergy - The allergy intolerance resource
 * @param index - The index of the reaction
 * @returns The reaction at the specified index, or undefined
 */
export function getReaction(
  allergy: AllergyIntolerance,
  index: number
): AllergyIntoleranceReaction | undefined {
  return allergy.reaction?.[index];
}

/**
 * Get manifestation from a specific reaction.
 *
 * @param reaction - The reaction resource
 * @returns The manifestation codeable concept, or undefined
 */
export function getReactionManifestation(
  reaction: AllergyIntoleranceReaction
): CodeableConcept | undefined {
  return reaction.manifestation?.[0];
}

/**
 * Get severity from a specific reaction.
 *
 * @param reaction - The reaction resource
 * @returns The severity coding, or undefined
 */
export function getReactionSeverity(
  reaction: AllergyIntoleranceReaction
): Coding | undefined {
  // First try to get from extension (full severity coding)
  const extension = reaction.extension?.find(
    (ext) => ext.url === ALLERGY_INTOLERANCE_SEVERITY_EXTENSION_URL
  );
  if (extension?.valueCoding) {
    return extension.valueCoding;
  }
  // Fallback to standard severity field
  if (reaction.severity) {
    return {
      code: reaction.severity,
      system: SYSTEMS.SNOMED,
    };
  }
  return undefined;
}

export function getOnsetDate(allergy: AllergyIntolerance): Date | null {
  if (!allergy.onsetDateTime) {
    return null;
  }
  return new Date(allergy.onsetDateTime);
}

export function getEncounterId(
  allergy: AllergyIntolerance
): string | undefined {
  return allergy.encounter?.reference?.replace('Encounter/', '');
}

// =============================================================================
// SETTERS - Immutable updates returning new AllergyIntolerance
// =============================================================================

export function toFhirDateString(date: Date | null): string | undefined {
  return date ? date.toISOString().split('T')[0] : undefined;
}

export function setAllergyCode(
  allergy: AllergyIntolerance,
  code: CodeableConcept | undefined
): AllergyIntolerance {
  return { ...allergy, code };
}

export function setCategory(
  allergy: AllergyIntolerance,
  category: AllergyIntolerance['category'] | undefined
): AllergyIntolerance {
  return { ...allergy, category };
}

export function setClinicalStatus(
  allergy: AllergyIntolerance,
  clinicalStatus: CodeableConcept | undefined
): AllergyIntolerance {
  return { ...allergy, clinicalStatus };
}

export function setSeverity(
  allergy: AllergyIntolerance,
  severity: Coding | undefined
): AllergyIntolerance {
  const existingReaction = allergy.reaction?.[0];

  if (!severity) {
    // Remove severity if it exists
    if (existingReaction) {
      const {
        severity: _severity,
        extension: _extension,
        ...restReaction
      } = existingReaction;
      const filteredExtension = existingReaction.extension?.filter(
        (ext) => ext.url !== ALLERGY_INTOLERANCE_SEVERITY_EXTENSION_URL
      );
      return {
        ...allergy,
        reaction:
          Object.keys(restReaction).length > 0 || filteredExtension?.length
            ? [
                {
                  ...restReaction,
                  manifestation: existingReaction.manifestation || [],
                  extension: filteredExtension,
                },
              ]
            : undefined,
      };
    }
    return allergy;
  }

  // Map severity to FHIR standard severity
  const mappedSeverity = mapSeverityToFhir(severity);

  const reaction = existingReaction || { manifestation: [] };
  const extension = [
    {
      url: ALLERGY_INTOLERANCE_SEVERITY_EXTENSION_URL,
      valueCoding: severity,
    },
  ];

  return {
    ...allergy,
    reaction: [
      {
        ...reaction,
        manifestation: reaction.manifestation || [],
        severity: mappedSeverity,
        extension,
      },
    ],
  };
}

export function setManifestation(
  allergy: AllergyIntolerance,
  manifestation: CodeableConcept | undefined
): AllergyIntolerance {
  const existingReaction = allergy.reaction?.[0];

  if (!manifestation) {
    // If no manifestation, ensure we still have an empty array (required by FHIR)
    if (existingReaction) {
      const { manifestation: _manifestation, ...restReaction } =
        existingReaction;
      return {
        ...allergy,
        reaction:
          Object.keys(restReaction).length > 0
            ? [{ ...restReaction, manifestation: [] }]
            : undefined,
      };
    }
    return allergy;
  }

  const reaction = existingReaction || { manifestation: [] };
  return {
    ...allergy,
    reaction: [
      {
        ...reaction,
        manifestation: [manifestation],
      },
    ],
  };
}

/**
 * Set all reactions for an allergy.
 *
 * @param allergy - The allergy intolerance resource
 * @param reactions - Array of reactions to set
 * @returns Updated allergy intolerance resource
 */
export function setReactions(
  allergy: AllergyIntolerance,
  reactions: AllergyIntoleranceReaction[]
): AllergyIntolerance {
  return {
    ...allergy,
    reaction: reactions.length > 0 ? reactions : undefined,
  };
}

/**
 * Add a new reaction to an allergy.
 *
 * @param allergy - The allergy intolerance resource
 * @param reaction - The reaction to add
 * @returns Updated allergy intolerance resource
 */
export function addReaction(
  allergy: AllergyIntolerance,
  reaction: AllergyIntoleranceReaction
): AllergyIntolerance {
  const existingReactions = allergy.reaction || [];
  return {
    ...allergy,
    reaction: [...existingReactions, reaction],
  };
}

/**
 * Update a reaction at a specific index.
 *
 * @param allergy - The allergy intolerance resource
 * @param index - The index of the reaction to update
 * @param reaction - The updated reaction
 * @returns Updated allergy intolerance resource
 */
export function updateReaction(
  allergy: AllergyIntolerance,
  index: number,
  reaction: AllergyIntoleranceReaction
): AllergyIntolerance {
  const existingReactions = allergy.reaction || [];
  if (index < 0 || index >= existingReactions.length) {
    return allergy;
  }
  const updated = [...existingReactions];
  updated[index] = reaction;
  return {
    ...allergy,
    reaction: updated,
  };
}

/**
 * Remove a reaction at a specific index.
 *
 * @param allergy - The allergy intolerance resource
 * @param index - The index of the reaction to remove
 * @returns Updated allergy intolerance resource
 */
export function removeReaction(
  allergy: AllergyIntolerance,
  index: number
): AllergyIntolerance {
  const existingReactions = allergy.reaction || [];
  if (index < 0 || index >= existingReactions.length) {
    return allergy;
  }
  const updated = existingReactions.filter((_, i) => i !== index);
  return {
    ...allergy,
    reaction: updated.length > 0 ? updated : undefined,
  };
}

/**
 * Update a reaction's manifestation at a specific index.
 *
 * @param allergy - The allergy intolerance resource
 * @param reactionIndex - The index of the reaction to update
 * @param manifestation - The manifestation codeable concept
 * @returns Updated allergy intolerance resource
 */
export function setReactionManifestation(
  allergy: AllergyIntolerance,
  reactionIndex: number,
  manifestation: CodeableConcept | undefined
): AllergyIntolerance {
  const existingReactions = allergy.reaction || [];
  if (reactionIndex < 0 || reactionIndex >= existingReactions.length) {
    return allergy;
  }

  const reaction = existingReactions[reactionIndex];
  // Ensure manifestation is always an array (required by FHIR)
  const updatedReaction: AllergyIntoleranceReaction = {
    ...reaction,
    manifestation: manifestation ? [manifestation] : [],
  };

  return updateReaction(allergy, reactionIndex, updatedReaction);
}

/**
 * Update a reaction's severity at a specific index.
 *
 * @param allergy - The allergy intolerance resource
 * @param reactionIndex - The index of the reaction to update
 * @param severity - The severity coding
 * @returns Updated allergy intolerance resource
 */
export function setReactionSeverity(
  allergy: AllergyIntolerance,
  reactionIndex: number,
  severity: Coding | undefined
): AllergyIntolerance {
  const existingReactions = allergy.reaction || [];
  if (reactionIndex < 0 || reactionIndex >= existingReactions.length) {
    return allergy;
  }

  const reaction = existingReactions[reactionIndex];
  let updatedReaction: AllergyIntoleranceReaction;

  if (!severity) {
    // Remove severity but keep manifestation
    const { severity: _severity, extension: _extension, ...rest } = reaction;
    const filteredExtension = reaction.extension?.filter(
      (ext) => ext.url !== ALLERGY_INTOLERANCE_SEVERITY_EXTENSION_URL
    );
    updatedReaction = {
      ...rest,
      manifestation: reaction.manifestation || [],
      extension:
        filteredExtension && filteredExtension.length > 0
          ? filteredExtension
          : undefined,
    };
  } else {
    // Map severity to FHIR standard severity
    const mappedSeverity = mapSeverityToFhir(severity);
    const extension = [
      {
        url: ALLERGY_INTOLERANCE_SEVERITY_EXTENSION_URL,
        valueCoding: severity,
      },
    ];
    updatedReaction = {
      ...reaction,
      manifestation: reaction.manifestation || [],
      severity: mappedSeverity,
      extension: [
        ...(reaction.extension || []).filter(
          (ext) => ext.url !== ALLERGY_INTOLERANCE_SEVERITY_EXTENSION_URL
        ),
        ...extension,
      ],
    };
  }

  return updateReaction(allergy, reactionIndex, updatedReaction);
}

export function setOnsetDate(
  allergy: AllergyIntolerance,
  date: Date | null
): AllergyIntolerance {
  return {
    ...allergy,
    onsetDateTime: toFhirDateString(date),
  };
}

// =============================================================================
// FACTORY - Create new AllergyIntolerance
// =============================================================================

export function createNewAllergyIntolerance(
  patient: Patient,
  encounter: Encounter
): AllergyIntolerance {
  return addProfileToResource(
    {
      resourceType: 'AllergyIntolerance',
      patient: createReference(patient),
      encounter: createReference(encounter),
      type: 'allergy',
      clinicalStatus: {
        coding: [
          {
            system: HL7.TERMINOLOGY.CS.ALLERGY_INTOLERANCE.CLINICAL.BASE,
            code: HL7.TERMINOLOGY.CS.ALLERGY_INTOLERANCE.CLINICAL.CODES.ACTIVE,
            display: 'Ενεργή',
          },
        ],
      },
    },
    HL7.ORG.SD.ALLERGY_INTOLERANCE
  );
}

// =============================================================================
// HELPERS - Convenience functions
// =============================================================================

export function hasAllergyCode(allergy: AllergyIntolerance): boolean {
  const code = getAllergyCode(allergy);
  return Boolean(code?.coding?.[0]?.code || code?.text);
}

export function hasClinicalStatus(allergy: AllergyIntolerance): boolean {
  const status = getClinicalStatus(allergy);
  return Boolean(status?.coding?.[0]?.code);
}

/**
 * Maps a severity Coding to FHIR standard severity values ('mild' | 'moderate' | 'severe').
 * This is used when saving to the server, as FHIR requires the standard severity.
 *
 * @param severity - The severity coding to map
 * @returns The mapped FHIR severity value, or undefined
 */
function mapSeverityToFhir(
  severity: Coding
): 'mild' | 'moderate' | 'severe' | undefined {
  const mapping: Record<string, 'mild' | 'moderate' | 'severe'> = {
    [EXTERNAL_CS.SNOMED.CODES.ALLERGY_SEVERITY_MILD]: 'mild',
    [EXTERNAL_CS.SNOMED.CODES.ALLERGY_SEVERITY_MILD_TO_MODERATE]: 'mild',
    [EXTERNAL_CS.SNOMED.CODES.ALLERGY_SEVERITY_MODERATE]: 'moderate',
    [EXTERNAL_CS.SNOMED.CODES.ALLERGY_SEVERITY_MODERATE_TO_SEVERE]: 'moderate',
    [EXTERNAL_CS.SNOMED.CODES.ALLERGY_SEVERITY_SEVERE]: 'severe',
    [EXTERNAL_CS.SNOMED.CODES.ALLERGY_SEVERITY_LIFE_THREATENING]: 'severe',
  };

  if (!severity?.code) {
    return undefined;
  }

  return mapping[severity.code];
}

/**
 * Prepare an AllergyIntolerance for saving to the server.
 * Validates and ensures all required fields are properly set.
 *
 * @param patient - The patient resource
 * @param encounter - The encounter resource
 * @param allergy - The allergy intolerance to prepare
 * @returns The prepared allergy intolerance resource
 */
export function prepareAllergyForSave(
  patient: Patient,
  encounter: Encounter,
  allergy: AllergyIntolerance
): AllergyIntolerance {
  return {
    ...allergy,
    resourceType: 'AllergyIntolerance',
    patient: allergy.patient ?? createReference(patient),
    encounter: allergy.encounter ?? createReference(encounter),
    type: allergy.type ?? 'allergy',
  };
}

export const getStatusColor = (status?: string) => {
  switch (status) {
    case 'active':
      return 'red';
    case 'resolved':
      return 'green';
    case 'inactive':
      return 'gray';
    default:
      return 'blue';
  }
};
